import React, { useEffect } from "react";
import { useFieldArray } from "react-hook-form";
import { dateToString } from "~/utils/formatter";
import { useTranslation } from "react-i18next";

//Hooks
import useRequest from "~/common/hooks/useRequest";

//Constants
import { API_STATE } from "~/common/constants/state";
import { STEP_OPTIONS } from "../CommonEditView/index";

//Components
import Form, { Group } from "~/common/components/Form";
import Input from "~/common/components/Input";
import DatePicker from "~/common/components/DatePicker";
import Tag from "~/common/components/Tag";
import Address from "~/common/components/Address";
import NumberInput from "~/common/components/NumberInput";
import Select from "~/common/components/Select";
import { UPLOAD_TYPE } from "~/common/constants/upload";
import MultiImageUploader from "~/common/components/FormImageUploader/MultiImageUploader";

//Assets
import "~/assets/sass/pages/portfolio/portfolioEdit.scss";
import { PlusIcon } from "~/assets/svg/MathIcon";
import { QuestionMarkIcon, RoundMinusIcon } from "~/assets/svg/InterfaceIcon";
import MultiCheckboxSelect from "~/common/components/MultiCheckboxSelect";

const BasicInfo = ({ useForm, industryOptions = [] }) => {
	const companyInfoRequest = useRequest("patch", "companies/company/num", "em");
	const { t, i18n } = useTranslation();

	const {
		fields: vcInvestsFields,
		append: vcInvestsAppend,
		replace: vcInvestsReplace,
		update: vcInvestsUpdate,
		remove: vcInvestsRemove,
	} = useFieldArray({
		control: useForm.control,
		name: "vcInvests",
	});

	const {
		fields: vcWithdrawFields,
		append: vcWithdrawAppend,
		replace: vcWithdrawReplace,
		remove: vcWithdrawRemove,
	} = useFieldArray({
		control: useForm.control,
		name: "vcWithdraws",
	});

	const handleClickLoadInfoBtn = () => {
		const bizNum = useForm.getValues("regNum");
		if (bizNum === undefined) {
			alert(t("pages.portfolio.PortfolioEdit.components.BasicInfo.enrollCompanyNum"));
		} else {
			companyInfoRequest.call({
				bizNum,
			});
		}
	};

	const handleAddVcInvest = () => {
		vcInvestsAppend({
			value: "",
			step: "",
			comValue: "",
			date: dateToString(new Date(), "yyyy-MM-dd"),
		});
	};

	const handleAddVcWithdraw = () => {
		vcWithdrawAppend({
			value: "",
			step: "",
			comValue: "",
			withdrawDate: dateToString(new Date(), "yyyy-MM-dd"),
		});
	};

	const handleRemoveVcInvest = (index) => {
		if (vcInvestsFields.length <= 0) return;
		vcInvestsRemove(index);
	};

	const handleRemoveVcWithdraw = (index) => {
		if (vcWithdrawFields.length <= 0) return;
		vcWithdrawRemove(index);
	};

	const handleInvestStatus = (status) => {
		if (useForm.watch().investStatus === "계획없음" && status === "계획없음") {
			useForm.setValue("investStatus", "진행중");
		} else {
			useForm.setValue("investStatus", status);
		}
	};

	useEffect(() => {
		switch (companyInfoRequest.state) {
			case API_STATE.done:
				const data = companyInfoRequest.response?.data;
				if (data) {
					console.log("data > ", data);
					useForm.setValue("name", data.companyName);
					useForm.setValue("ceoName", data.representationName);
					useForm.setValue("industryCondition", data.businessCategoryDesc);
					useForm.setValue("address", data.address);
					useForm.setValue("employeeNum", data.employeeCount);
					useForm.setValue("homepage", data.homepage);
					data.logoUrl !== "" && useForm.setValue("logoSqu", [data.logoUrl]);
					useForm.setValue("corpNum", data.corporationNum);
					useForm.setValue("estDate", new Date(data.createDate));
					if (data.keyword && data.keyword !== "") {
						useForm.setValue("portfolioKeyword", data.keyword.split(","));
					}
				} else {
					alert(t("pages.portfolio.PortfolioEdit.components.BasicInfo.noData"));
				}
				debugger;
				break;
			case API_STATE.error:
				switch (companyInfoRequest.error.statusCode) {
					case 100:
						alert(t("pages.portfolio.PortfolioEdit.components.BasicInfo.noData"));
						break;
					case 101:
						alert(t("pages.portfolio.PortfolioEdit.components.BasicInfo.noData"));
						break;
				}
			default:
				break;
		}
	}, [companyInfoRequest.state, companyInfoRequest.response]);

	return (
		<ul className="basic-info-wrap">
			<li className="company-info">
				<Form useForm={useForm}>
					<MultiImageUploader
						name="logoSqu"
						type={UPLOAD_TYPE.COMPANY_LOGO}
						width="100px"
						height="100px"
						className={"product-img"}
						maxCount={1}
					/>
					<p className="company-name">
						{useForm.watch().name ||
							t("pages.portfolio.PortfolioEdit.components.BasicInfo.companyName")}
					</p>
					<p className="company-url">
						{useForm.watch().homepage ||
							t("pages.portfolio.PortfolioEdit.components.BasicInfo.hpAddress")}
					</p>
				</Form>
			</li>
			{/* 기업 정보 */}
			<li>
				<div className="title-group">
					<h3 className="basic-info-title">
						{t("pages.portfolio.PortfolioEdit.components.BasicInfo.companyInfo")}
						<div className="c__tooltip-btn">
							<QuestionMarkIcon />
							<p className="c__tooltip short">
								투자를 할 기업의 기본 정보를 입력할 수 있습니다.
							</p>
						</div>
					</h3>
				</div>
				<Form useForm={useForm} className="vertical">
					<Group
						label={t(
							"pages.portfolio.PortfolioEdit.components.BasicInfo.companyNumber"
						)}
						className="registration-num"
						required
					>
						<NumberInput
							name="regNum"
							thousandSeparator={false}
							type="company"
							required
						/>
						<button
							type="button"
							className="btn__outline angled btn-api"
							color="primary"
							onClick={handleClickLoadInfoBtn}
						>
							{t("pages.portfolio.PortfolioEdit.components.BasicInfo.loadInfo")}
						</button>
					</Group>
					<NumberInput
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.corpoNum")}
						name="corpNum"
						thousandSeparator={false}
					/>
					<Input
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.companyName")}
						name="name"
						value={useForm.watch().name}
						required
					/>
					<Input
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.present")}
						name="ceoName"
						value={useForm.watch().ceoName}
						required
					/>
					<Input
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.type")}
						name="industryCondition"
						value={useForm.watch().industryCondition}
					/>
					<Address
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.location")}
						name={{
							postCode: "zipCode",
							address1: "address",
							address2: "addressDetail",
						}}
					/>
					<NumberInput
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.employeeNum")}
						name="employeeNum"
					/>
					<DatePicker
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.estDate")}
						name="estDate"
					/>
					<Input
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.hp")}
						name="homepage"
					/>
					<Group
						className={"keyword-wrap"}
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.field")}
					>
						<MultiCheckboxSelect
							valueKey={"name"}
							name="industry"
							options={industryOptions}
							selectionLimit={5}
							placeholder={t(
								"pages.portfolio.PortfolioEdit.components.BasicInfo.choiceFiled"
							)}
							// {...useForm.register(`industry`, {
							//   onChange: (e)=> {
							//     // debugger;
							//   }
							// })}
							// selectedValues={[{idx: 1}, {idx: 2}, {idx: 3}]}
							// selectedValues={[{idx:1, name: "생활"},{idx:2, name: "음식"},{idx:3, name: "빅데이터"}]}
							selectedValues={useForm.watch().industry}
							className="f__column"
						/>
						<p className="notice">
							{t("pages.portfolio.PortfolioEdit.components.BasicInfo.maxFive")}
						</p>
					</Group>
					<Tag
						label={t("pages.portfolio.PortfolioEdit.components.BasicInfo.keyword")}
						name="portfolioKeyword"
						className="c__tag-wrap"
						{...useForm.register(`portfolioKeyword`, {
							// onChange: (e) => {
							//   debugger;
							// }
						})}
					/>
				</Form>
			</li>

			{/* 투자 계획 */}
			<li>
				<div className="title-group">
					<h3 className="basic-info-title">
						{t("pages.portfolio.PortfolioEdit.components.BasicInfo.planInvest")}
						<div className="c__tooltip-btn">
							<QuestionMarkIcon />
							<p className="c__tooltip long">
								진행 상태 별(진행중/완료) 해당 기업의 투자 계획을 작성할 수
								있습니다.
								<br />
								투자가 완료된 경우 투자 이력에 해당 내용을 업데이트해 주시면 됩니다.
								<br />
								<br />
								<div className="bold">[진행 상태]</div>
								- 진행 중 : 투자가 진행 중인 상태입니다.
								<br />- 완료 : 투자가 완료된 상태입니다.
							</p>
						</div>
					</h3>
					<div className="switch-wrap">
						<label className="switch">
							<input
								type="checkbox"
								onChange={() => handleInvestStatus("계획없음")}
								checked={useForm.watch().investStatus !== "계획없음"}
							/>
							<span className="slider round" />
						</label>
					</div>
				</div>
				<div className="basic-info-content">
					{useForm.watch().investStatus === "계획없음" ? (
						<div className="no-data">
							스타트업의 투자유치 계획을 작성할 수 있습니다.
						</div>
					) : (
						<Form useForm={useForm} className="vertical">
							<div className="f__group export-input ">
								<div className="f__control  ">
									<div className="f__item c__radio-wrap">
										<label className="c__radio">
											<input
												type="radio"
												checked={useForm.watch().investStatus === "진행중"}
												onChange={() => handleInvestStatus("진행중")}
												value={t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.onGoing"
												)}
											/>
											<span className="checkmark" />
											<span className="radio-label">
												{t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.onGoing"
												)}
											</span>
										</label>
										<label className="c__radio">
											<input
												type="radio"
												checked={useForm.watch().investStatus === "완료"}
												onChange={() => handleInvestStatus("완료")}
												value={t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.onGoing"
												)}
											/>
											<span className="checkmark" />
											<span className="radio-label">
												{t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.clear"
												)}
											</span>
										</label>
									</div>
								</div>
							</div>

							<Select
								label={t(
									"pages.portfolio.PortfolioEdit.components.BasicInfo.scaleInvest"
								)}
								name="round"
								options={STEP_OPTIONS}
								defaultValue={useForm.watch().round}
							/>
							<NumberInput
								label={
									<>
										{t(
											"pages.portfolio.PortfolioEdit.components.BasicInfo.planAmountInvest"
										)}
										<span className="title-unit">
											{t(
												"pages.portfolio.PortfolioEdit.components.BasicInfo.unit"
											)}
										</span>
									</>
								}
								name="target"
								decimalScale={2}
								fixedDecimalScale={false}
							/>
							<NumberInput
								label={
									<>
										{t(
											"pages.portfolio.PortfolioEdit.components.BasicInfo.fixedValue"
										)}
										<span className="title-unit">
											{t(
												"pages.portfolio.PortfolioEdit.components.BasicInfo.unit"
											)}
										</span>
									</>
								}
								name="comValue"
								decimalScale={2}
								fixedDecimalScale={false}
							/>
						</Form>
					)}
				</div>
			</li>

			{/* 투자 이력 */}
			<li>
				<div className="title-group">
					<h3 className="basic-info-title">
						{t("pages.portfolio.PortfolioEdit.components.BasicInfo.investHistory")}
						<div className="c__tooltip-btn">
							<QuestionMarkIcon />
							<p className="c__tooltip short">VC의 투자 이력을 추가할 수 있습니다.</p>
						</div>
					</h3>
					<button
						type="button"
						className="dashed-btn plus-btn"
						onClick={handleAddVcInvest}
					>
						<PlusIcon />
						{t("pages.portfolio.PortfolioEdit.components.BasicInfo.historyAdd")}
					</button>
				</div>
				{vcInvestsFields.map((vcInvest, index) => {
					return (
						<div
							key={vcInvest.id}
							className={`row-wrap dynamic-form-wrap ${
								i18n.language === "ko" ? "" : "vertical"
							}`}
						>
							<Form useForm={useForm} className="vertical">
								<DatePicker
									label={t(
										"pages.portfolio.PortfolioEdit.components.BasicInfo.investPoint"
									)}
									name={`vcInvests.${index}.date`}
									value={useForm.watch().vcInvests[index].date}
									{...useForm.register(`vcInvests.${index}.date`, {})}
									maxDate={new Date()}
								/>
								<NumberInput
									label={
										<>
											{t(
												"pages.portfolio.PortfolioEdit.components.BasicInfo.valueAtTime"
											)}
											<span className="title-unit">
												{t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.unit"
												)}
											</span>
										</>
									}
									// name="timePointValue"
									placeholder={t(
										"pages.portfolio.PortfolioEdit.components.BasicInfo.valueAtTime"
									)}
									decimalScale={2}
									fixedDecimalScale={false}
									value={useForm.watch().vcInvests[index].comValue}
									{...useForm.register(`vcInvests.${index}.comValue`)}
								/>
								<Select
									label={t(
										"pages.portfolio.PortfolioEdit.components.BasicInfo.investStage"
									)}
									// name="purposeRound"
									options={STEP_OPTIONS}
									// defaultValue="PreSeed"
									defaultValue={useForm.watch().vcInvests[index].step}
									{...useForm.register(`vcInvests.${index}.step`)}
								/>
								<NumberInput
									label={
										<>
											{t(
												"pages.portfolio.PortfolioEdit.components.BasicInfo.investAmount"
											)}
											<span className="title-unit">
												{t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.unit"
												)}
											</span>
										</>
									}
									// name="investPrice"
									placeholder={t(
										"pages.portfolio.PortfolioEdit.components.BasicInfo.investAmount"
									)}
									decimalScale={2}
									fixedDecimalScale={false}
									value={useForm.watch().vcInvests[index].value}
									{...useForm.register(`vcInvests.${index}.value`)}
								/>
							</Form>
							{vcInvestsFields?.length > 0 && (
								<button
									type="button"
									className="btn-round-delete"
									onClick={() => {
										handleRemoveVcInvest(index);
									}}
								>
									<RoundMinusIcon />
								</button>
							)}
						</div>
					);
				})}
			</li>

			{/* 회수 이력 */}
			<li>
				<div className="title-group">
					<h3 className="basic-info-title">
						{t("pages.portfolio.PortfolioEdit.components.BasicInfo.withdrawHistory")}
						<div className="c__tooltip-btn">
							<QuestionMarkIcon />
							<p className="c__tooltip short">VC의 회수 이력을 추가할 수 있습니다.</p>
						</div>
					</h3>
					<button
						type="button"
						className="dashed-btn plus-btn"
						onClick={handleAddVcWithdraw}
					>
						<PlusIcon />
						{t("pages.portfolio.PortfolioEdit.components.BasicInfo.historyAdd")}
					</button>
				</div>
				{vcWithdrawFields.map((vcWithdraw, index) => {
					return (
						<div
							key={vcWithdraw.id}
							className={`row-wrap dynamic-form-wrap ${
								i18n.language === "ko" ? "" : "vertical"
							}`}
						>
							<Form useForm={useForm} className="vertical">
								<DatePicker
									label={t(
										"pages.portfolio.PortfolioEdit.components.BasicInfo.withdrawPoint"
									)}
									// name="date"
									value={useForm.watch().vcWithdraws[index].withdrawDate}
									{...useForm.register(`vcWithdraws.${index}.withdrawDate`)}
									maxDate={new Date()}
								/>
								<NumberInput
									label={
										<>
											{t(
												"pages.portfolio.PortfolioEdit.components.BasicInfo.withdrawAmount"
											)}
											<span className="title-unit">
												{t(
													"pages.portfolio.PortfolioEdit.components.BasicInfo.unit"
												)}
											</span>
										</>
									}
									// name="investPrice"
									placeholder={t(
										"pages.portfolio.PortfolioEdit.components.BasicInfo.withdrawAmount"
									)}
									decimalScale={2}
									fixedDecimalScale={false}
									value={useForm.watch().vcWithdraws[index].value}
									{...useForm.register(`vcWithdraws.${index}.value`)}
								/>
							</Form>
							{vcWithdrawFields?.length > 0 && (
								<button
									type="button"
									className="btn-round-delete"
									onClick={() => {
										handleRemoveVcWithdraw(index);
									}}
								>
									<RoundMinusIcon />
								</button>
							)}
						</div>
					);
				})}
			</li>
		</ul>
	);
};

export default BasicInfo;
